import { TwitchatDataTypes } from "@/types/TwitchatDataTypes";
import type { StoreActions, StoreGetters } from "@/types/pinia-helpers";
import ApiHelper from "@/utils/ApiHelper";
import Config from "@/utils/Config";
import SetIntervalWorker from "@/utils/SetIntervalWorker";
import Utils from "@/utils/Utils";
import TwitchUtils from "@/utils/twitch/TwitchUtils";
import { acceptHMRUpdate, defineStore } from "pinia";
import DataStore from "../DataStore";
import type { ITipeeeActions, ITipeeeGetters, ITipeeeState } from "../StoreProxy";
import StoreProxy from "../StoreProxy";

let socket: WebSocket | undefined = undefined;
let pingInterval: string = "";
let reconnectTimeout: number = -1;
let reconnectAttempts: number = 0;
let isAutoInit: boolean = false;
let autoReconnect: boolean = false;

export const storeTipeee = defineStore("tipeee", {
	state: (): ITipeeeState => ({
		me: null,
		accessToken: "",
		refreshToken: "",
		connected: false,
		authResult: { code: "", csrf: "" },
	}),

	getters: {} satisfies StoreGetters<ITipeeeGetters, ITipeeeState>,

	actions: {
		async populateData(): Promise<void> {
			const data = DataStore.get(DataStore.TIPEEE);
			if (data) {
				const json = JSON.parse(data) as TipeeStoreData;
				this.accessToken = json.accessToken;
				this.refreshToken = json.refreshToken;
				if (this.refreshToken) {
					isAutoInit = true;
					await this.doRefreshToken();
				}
			}
		},

		async doRefreshToken(): Promise<void> {
			const result = await ApiHelper.call(
				"tipeee/refreshToken",
				"POST",
				{ refreshToken: this.refreshToken },
				false,
			);
			if (result.json.success) {
				this.accessToken = result.json.accessToken!;
				this.refreshToken = result.json.refreshToken!;
				this.saveData();
				window.setTimeout(() => {
					void this.doRefreshToken();
				}, 3600 * 1000);
				//Tipeee do not returns expireas at for refresh token. Refresh every hour :/
				void this.connect(this.accessToken, false);
			} else {
				StoreProxy.common.alert(StoreProxy.i18n.t("error.tipeee_connect_failed"));
			}
		},

		async getOAuthURL(): Promise<string> {
			const csrfToken = await ApiHelper.call("auth/CSRFToken", "GET");
			const origin = Config.instance.IS_PROD
				? document.location.origin
				: "https://dev.twitchat.fr";
			const redirectURI = origin + StoreProxy.router.resolve({ name: "tipeee/auth" }).href;
			const url = new URL("https://api.tipeeestream.com/oauth/v2/auth");
			url.searchParams.set("client_id", Config.instance.TIPEEE_CLIENT_ID);
			url.searchParams.set("redirect_uri", redirectURI);
			url.searchParams.set("response_type", "code");
			url.searchParams.set("state", csrfToken.json.token);
			return url.href;
		},

		setAuthResult(code: string, csrf: string): void {
			this.authResult.code = code;
			this.authResult.csrf = csrf;
		},

		async completeOAuthProcess(): Promise<boolean> {
			try {
				const csrfResult = await ApiHelper.call("auth/CSRFToken", "POST", {
					token: this.authResult.csrf,
				});
				if (!csrfResult.json.success) return false;
				const result = await ApiHelper.call("tipeee/auth", "POST", this.authResult, false);
				if (result.json.success) {
					this.accessToken = result.json.accessToken!;
					this.refreshToken = result.json.refreshToken!;
					this.saveData();
					this.authResult = { code: "", csrf: "" };
					return await this.connect(this.accessToken);
				}
				return false;
			} catch (_error) {
				return false;
			}
		},

		async connect(token: string, isReconnect: boolean = false): Promise<boolean> {
			if (!StoreProxy.auth.isPremium) return false;

			//Token changed
			if (isReconnect && token != this.accessToken) return false;

			this.disconnect(false);

			autoReconnect = true;

			//Get user's details
			//This query is probably unnecessary as the username can apparently be anything on "join-room" event
			const meUrl = new URL("https://api.tipeeestream.com/v1.0/me.json");
			meUrl.searchParams.set("access_token", this.accessToken);
			const meQuery = await fetch(meUrl, { method: "GET" });
			const meJSON = (await meQuery.json()) as TipeeeMeData;
			this.me = {
				avatar:
					"https://www.tipeeestream.com/cdn-cgi/image/onerror=redirect,width=64,height=64,fit=cover/" +
					meJSON.avatar.original_path.replace(/\\\//, "/"),
				currency: meJSON.currency.code,
				id: meJSON.id,
				username: meJSON.username,
			};

			//Get user's API key
			const accessTokenUrl = new URL("https://api.tipeeestream.com/v1.0/me/api.json");
			accessTokenUrl.searchParams.set("access_token", this.accessToken);
			const accessTokenQuery = await fetch(accessTokenUrl, { method: "GET" });
			const accessTokenJSON = (await accessTokenQuery.json()) as { apiKey: string };

			//Get socket path
			const socketPropsQuery = await fetch("https://api.tipeeestream.com/v2.0/site/socket", {
				method: "GET",
			});
			const socketProps = (await socketPropsQuery.json()) as {
				code: number;
				datas: { port: string; host: string };
			};

			let socketPath = socketProps.datas.host.replace(/https?:\/\//, "");
			if (socketProps.datas.port && socketProps.datas.port != "443")
				socketPath += ":" + socketProps.datas.port;

			socket = new WebSocket(
				`wss://${socketPath}/socket.io/?EIO=3&transport=websocket&access_token=${accessTokenJSON.apiKey}`,
			);

			socket.onopen = async () => {
				reconnectAttempts = 0;
				clearTimeout(reconnectTimeout);
			};

			return new Promise<boolean>((resolve, _reject) => {
				socket = socket!; //Enforce non-null for typescript
				socket.onmessage = (event: MessageEvent<string>) => {
					//PONG messages
					if (event.data == "3") return;
					//Connection acknwoledgment
					if (event.data == "5") {
						resolve(true);

						if (pingInterval) SetIntervalWorker.instance.delete(pingInterval);
						pingInterval = SetIntervalWorker.instance.create(() => {
							socket?.send("2");
						}, 25000);

						return;
					}
					try {
						let codeLength = 0;
						for (; codeLength < event.data.length; codeLength++) {
							if (!/[0-9]/.test(event.data[codeLength]!)) break;
						}
						const code = event.data.substring(0, codeLength);
						const json = JSON.parse(
							event.data.replace(new RegExp("^" + code, ""), "") || "{}",
						);
						//Welcome message
						if (code == "0") {
							//Join room to get events
							socket?.send(
								`42["join-room",{"room":"${accessTokenJSON.apiKey}","username":"${meJSON.username}"}]`,
							);

							const json = JSON.parse(
								event.data.replace(new RegExp("^" + code, ""), ""),
							);
							const message = json as TipeeeWelcomeData;
							//Send PING command regularly
							if (pingInterval) SetIntervalWorker.instance.delete(pingInterval);
							pingInterval = SetIntervalWorker.instance.create(() => {
								if (
									socket?.readyState == socket?.CLOSING ||
									socket?.readyState == socket?.CLOSED
								) {
									// console.log("SOCKET STATE", socket?.readyState);
								} else {
									socket?.send("2");
								}
							}, message.pingInterval || 10000);
							this.connected = true;
							resolve(true);
						}

						//Connection acknowledgment
						else if (code == "40") return;

						//Authentication failed
						else if (code == "44") {
							//Show error on top of page
							if (isAutoInit) {
								StoreProxy.common.alert(
									StoreProxy.i18n.t("error.tipeee_connect_failed"),
								);
							}
							this.disconnect(false);
							resolve(false);
						} else {
							if (Array.isArray(json)) {
								const me = StoreProxy.auth.twitch.user;
								json.forEach((entry) => {
									if (typeof entry == "string") return;
									const message = (entry as TipeeeEventData).event;
									switch (message.type) {
										case "donation": {
											const chunks = TwitchUtils.parseMessageToChunks(
												message.parameters.message,
												undefined,
												true,
											);
											const data: TwitchatDataTypes.MessageTipeeeDonationData =
												{
													id: Utils.getUUID(),
													eventType: "donation",
													platform: "twitch",
													channel_id: me.id,
													type: TwitchatDataTypes.TwitchatMessageType
														.TIPEEE,
													date: Date.now(),
													amount: message.parameters.amount,
													amountFormatted:
														message.parameters.amount +
														message.parameters.currency,
													currency: message.parameters.currency,
													message: message.parameters.message,
													message_chunks: chunks,
													message_html:
														TwitchUtils.messageChunksToHTML(chunks),
													userName: message.parameters.username,
													recurring: message.parameters.recurring == 1,
													recurringCount:
														message.parameters.recurring_counter || 0,
												};
											void StoreProxy.chat.addMessage(data);
											break;
										}
									}
								});
							}
						}
					} catch (error) {
						console.log("Failed parsing Tipeee JSON data");
						console.log(event.data);
						console.error(error);
					}
					isAutoInit = false;
				};

				socket.onclose = (_event) => {
					//Do not reconnect if token changed
					if (token != this.accessToken) return;
					if (!autoReconnect) return;

					this.connected = false;
					if (pingInterval) SetIntervalWorker.instance.delete(pingInterval);
					clearTimeout(reconnectTimeout);
					reconnectAttempts++;
					reconnectTimeout = window.setTimeout(() => {
						socket = undefined;
						void this.connect(token, true);
					}, 500 * reconnectAttempts);
				};

				socket.onerror = (_error) => {
					resolve(false);
					this.connected = false;
					if (!autoReconnect) return;
					reconnectAttempts++;
					if (pingInterval) SetIntervalWorker.instance.delete(pingInterval);
					clearTimeout(reconnectTimeout);
					reconnectTimeout = window.setTimeout(() => {
						socket = undefined;
						void this.connect(token, true);
					}, 500 * reconnectAttempts);
				};
			});
		},

		disconnect(resetToken: boolean = true): void {
			autoReconnect = false;
			this.connected = false;
			if (resetToken) {
				this.accessToken = "";
				this.refreshToken = "";
				this.saveData();
			}
			if (pingInterval) SetIntervalWorker.instance.delete(pingInterval);
			clearTimeout(reconnectTimeout);
			if (socket && !this.connected) socket.close();
		},

		saveData(): void {
			const data: TipeeStoreData = {
				accessToken: this.accessToken,
				refreshToken: this.refreshToken,
			};
			DataStore.set(DataStore.TIPEEE, data);
		},
	} satisfies StoreActions<"tipeee", ITipeeeState, ITipeeeGetters, ITipeeeActions>,
});

if (import.meta.hot) {
	import.meta.hot.accept(acceptHMRUpdate(storeTipeee, import.meta.hot));
}

interface TipeeStoreData {
	accessToken: string;
	refreshToken: string;
}

interface TipeeeWelcomeData {
	pingInterval: number;
	pingTimeout: number;
	sid: string;
}

interface TipeeeEventData {
	event: {
		type: "donation";
		created_at: string;
		deleted_at: string;
		display: number;
		origin?: any;
		formattedAmount: number;
		parameters: {
			username: string;
			amount: number;
			message: string;
			formattedMessage: string;
			currency: string;
			recurring?: number;
			recurring_counter?: number;
		};
		"parameters.amount": number;
		ref: number;
		user: {
			locked: boolean;
			hash_id: number;
			hash: string;
			avatar: {
				id: number;
				path: string;
				code: string;
				is_converted: boolean;
				original_path: string;
			};
			hasPayment: boolean;
			currency: {
				code: string;
				symbol: string;
				label: string;
				available: boolean;
			};
			country: string;
			campaignActivation: number;
			id: number;
			providers: {
				connectedAt: string;
				code: string;
				id: string;
				username: string;
				master: boolean;
				followers: number;
				created_at: string;
				channel: string;
				expiration_at: string;
			}[];
			username: string;
			pseudo: string;
			email_confirmation_at: string;
			created_at: string;
			session_at: string;
			parameters: any[];
		};
	};
}

export type TipeeeMeData = {
	locked: boolean;
	hash_id: number;
	hash: string;
	avatar: {
		id: number;
		path: string;
		code: string;
		is_converted: boolean;
		original_path: string;
	};
	hasPayment: boolean;
	currency: {
		code: string;
		symbol: string;
		label: string;
		available: boolean;
	};
	country: string;
	campaignActivation: number;
	id: number;
	providers: Array<{
		connectedAt: string;
		code: string;
		id: string;
		username: string;
		master: boolean;
		followers: number;
		created_at: string;
		channel: string;
		expiration_at: string;
	}>;
	username: string;
	pseudo: string;
	created_at: string;
	session_at: string;
	parameters: Array<any>;
};
